// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// See ADDITIONAL-TERMS.md for the section 7(b) attribution requirement.
//
// modes.ts — the recipe graph, the three modes, and the eight quirks.
//
// THE TEST EVERY MODE HAD TO PASS: does a policy that WINS here LOSE in the other two? If two modes
// reward the same thumb, one of them is a difficulty slider and should be cut. tests/balance.test.ts
// asserts the sign FLIPS on a functioning pair rather than merely asserting the numbers differ:
// `steady` rewards `reference` over `nomad`, and `drought` rewards `nomad` over `reference`. Same
// pair, opposite sign. A mode that only moves a magnitude has not earned its place on the picker.
//
// FIVE ITEMS, NOT EIGHT, AND THE REASON IS ARITHMETIC RATHER THAN TASTE. The emergent bottleneck
// this game is built to produce is SHALE CONTENTION: a smelter wants 0.80 shale/s and a mill wants
// 0.40 shale/s, and they are physically in different places. A naive even split starves the smelter
// and backs up the mill, and the player has to DISCOVER that one armature costs 3 shale and 2 loam.
// One shared raw across two consumers, feeding one combiner, is the smallest graph that produces a
// ratio problem, a routing problem and a rut problem at the same cell. A sixth item would add a
// dock tile; it would not add a second contention.

/**
 * Cell kinds live here rather than in sim.ts so the recipe table can name them without a circular
 * import, and they are a frozen object rather than a `const enum` because `isolatedModules` (which
 * Vite requires) rejects const enums outright.
 */
export const Kind = {
  Empty: 0,
  Boulder: 1,
  Belt: 2,
  Wye: 3,
  Digger: 4,
  Smelter: 5,
  Mill: 6,
  Jig: 7,
  Wharf: 8,
} as const;
export type KindT = (typeof Kind)[keyof typeof Kind];

export const ITEMS = ['shale', 'loam', 'billet', 'binder', 'armature'] as const;
export type Item = 0 | 1 | 2 | 3 | 4;
export const ITEM_COUNT = 5;

/** Silhouettes, because type is NEVER encoded by hue. All five draw in one cream. */
export const ITEM_GLYPH = ['▲', '●', '▬', '◆', '✶'] as const;
export const ITEM_NAME = ['Shale', 'Loam', 'Billet', 'Binder', 'Armature'] as const;

export interface Recipe {
  /** Counts indexed by Item. */
  inputs: readonly number[];
  output: Item;
}

/**
 * All three crafters take the same 2.5s, so a 1:1:1 line is EXACT — one smelter, one mill and one
 * jig make 0.40 armature/s and demand 1.20 shale/s. That sits at 96% of one cell's free capacity,
 * which is why a single line is calm and a second line on the same lane burns it in about a minute.
 */
export const RECIPES: Record<number, Recipe | undefined> = {
  [Kind.Smelter]: { inputs: [2, 0, 0, 0, 0], output: 2 },
  [Kind.Mill]: { inputs: [1, 2, 0, 0, 0], output: 3 },
  [Kind.Jig]: { inputs: [0, 0, 1, 1, 0], output: 4 },
};

export const KIND_NAME: Record<number, string> = {
  [Kind.Belt]: 'Belt',
  [Kind.Wye]: 'Wye',
  [Kind.Digger]: 'Digger',
  [Kind.Smelter]: 'Smelter',
  [Kind.Mill]: 'Mill',
  [Kind.Jig]: 'Jig',
  [Kind.Wharf]: 'Wharf',
};

export const KIND_BLURB: Record<number, string> = {
  [Kind.Belt]: 'Carries items. Drag to lay a whole run.',
  [Kind.Wye]: 'Splits one lane into two or three, evenly.',
  [Kind.Digger]: 'Sits on a seam and digs. One item a second.',
  [Kind.Smelter]: '2 Shale into 1 Billet.',
  [Kind.Mill]: '1 Shale + 2 Loam into 1 Binder.',
  [Kind.Jig]: '1 Billet + 1 Binder into 1 Armature.',
  [Kind.Wharf]: 'Ships what reaches it. Armatures are the score.',
};

// ── quirks ───────────────────────────────────────────────────────────────────────────────────
// Exactly two are drawn per seed, from two DIFFERENT families, giving 24 combinations from eight
// one-line changes. This is the cheapest depth-per-byte in the game: `hardpan` + `thinloam` is
// cheap lanes carrying a resource you do not have, and `clay` + `rubble` is one lane per line on a
// board with no room for lanes.

export const QUIRK_FAMILY = ['ground', 'terrain', 'resource', 'geometry'] as const;
export type QuirkFamily = (typeof QUIRK_FAMILY)[number];

export interface Quirk {
  id: string;
  family: QuirkFamily;
  name: string;
  blurb: string;
}

export const QUIRKS: Quirk[] = [
  { id: 'hardpan', family: 'ground', name: 'Hardpan', blurb: 'The ground is tough. A lane carries 1.54 a second before it ruts.' },
  { id: 'clay', family: 'ground', name: 'Clay', blurb: 'The ground is soft. A lane carries only 1.00 a second before it ruts.' },
  { id: 'rubble', family: 'terrain', name: 'Rubble', blurb: 'Twenty-two boulders. Space, not dirt, is what runs out.' },
  { id: 'swept', family: 'terrain', name: 'Swept', blurb: 'Two boulders. Room everywhere, so rut is the only limit.' },
  { id: 'thinloam', family: 'resource', name: 'Thin Loam', blurb: 'One loam seam only. Binder is the leg that starves.' },
  { id: 'scattershale', family: 'resource', name: 'Scattershale', blurb: 'Five small shale seams. Many short lanes, many diggers.' },
  { id: 'shorthaul', family: 'geometry', name: 'Short Haul', blurb: 'A seam right beside the wharf. Rut bites inside a minute.' },
  { id: 'farflung', family: 'geometry', name: 'Far Flung', blurb: 'Every seam is a long way out. Belt scrip is the early gate.' },
];

export const quirkOf = (id: string): Quirk | undefined => QUIRKS.find((q) => q.id === id);
export const hasQuirk = (qs: Quirk[], id: string): boolean => qs.some((q) => q.id === id);

/** The ground quirks are the only ones that touch the rut law, and they touch exactly one constant. */
export function rutDeltaFor(qs: Quirk[]): number {
  if (hasQuirk(qs, 'hardpan')) return -3;
  if (hasQuirk(qs, 'clay')) return 4;
  return 0;
}

// ── modes ────────────────────────────────────────────────────────────────────────────────────

export interface Mode {
  id: string;
  name: string;
  ticks: number;
  startScrip: number;
  /** 0 = seams never run out. */
  patchReserve: number;
  loamReserve: number;
  /** Teardown: you may only re-place what you removed, and there is no income at all. */
  moveOnly: boolean;
  beltBudget: number;
  rutPerEntryDelta?: number;
  blurb: string;
  /** One line for the reference card. Every mode must state what differs. */
  rule: string;
}

export const MODES: Record<string, Mode> = {
  steady: {
    id: 'steady',
    name: 'Steady',
    ticks: 6 * 60 * 20,
    startScrip: 90,
    patchReserve: 0,
    loamReserve: 0,
    moveOnly: false,
    beltBudget: 999,
    blurb: 'Six minutes, seams that never run dry. The only thing that runs out is clean ground.',
    rule: 'Seams are infinite. Everything you spend comes back if you tear it up.',
  },
  drought: {
    id: 'drought',
    name: 'Drought',
    ticks: 6 * 60 * 20,
    startScrip: 90,
    patchReserve: 420,
    loamReserve: 380,
    moveOnly: false,
    beltBudget: 999,
    blurb: 'The seams are finite and they die one at a time, in an order you chose.',
    rule: 'Every seam holds a fixed amount. When it is gone its digger goes with it.',
  },
  teardown: {
    id: 'teardown',
    name: 'Teardown',
    ticks: 6 * 60 * 20,
    startScrip: 0,
    patchReserve: 0,
    loamReserve: 0,
    moveOnly: true,
    beltBudget: 0,
    blurb: 'Somebody else built this, badly, and left four minutes of rut in the ground. No money. Only moves.',
    rule: 'You cannot buy anything. You may only move what is already here.',
  },
};

export const MODE_IDS = ['steady', 'drought', 'teardown'] as const;
export const DEFAULT_MODE = 'steady';

/**
 * Validate an id off the wire or out of a URL. An unknown id must fall back, never reach the
 * generator as `undefined` — a wire-keyed lookup with `MODES[id] || DEFAULT` also lets
 * "constructor" through, so this uses Object.hasOwn.
 */
export function modeOf(id: string): Mode {
  return Object.hasOwn(MODES, id) ? MODES[id] : MODES[DEFAULT_MODE];
}
